import {
  PropsWithChildren,
  createContext,
  useContext,
  useEffect,
  useState,
} from "react";

import { captureException } from "@sentry/react-native";

import { Config } from "../env";
import { DeliveryStatus } from "../types";

type ContextType = {
  deliveryStatus: DeliveryStatus | null;
};

// Get the status every X miliseconds
const POLLING_INTERVAL = 30000;

const Context = createContext<ContextType | null>(null);

export default function useDeliveryStatus() {
  const ctx = useContext(Context);
  if (!ctx) throw new Error("Missing DeliveryStatusProvider");
  return ctx;
}

type ProviderProps = PropsWithChildren<{ uuid: string }>;
export function DeliveryStatusProvider({ uuid, children }: ProviderProps) {
  const [deliveryStatus, setDeliveryStatus] = useState<DeliveryStatus | null>(
    null
  );

  useEffect(() => {
    async function updateDeliveryStatus() {
      try {
        const deliveryStatus = await getDeliveryStatus(uuid);
        setDeliveryStatus(deliveryStatus);
      } catch (e) {
        captureException(e);
      }
    }
    updateDeliveryStatus();
    const id = setInterval(updateDeliveryStatus, POLLING_INTERVAL);
    return () => clearInterval(id);
  }, [uuid]);

  return (
    <Context.Provider value={{ deliveryStatus }}>{children}</Context.Provider>
  );
}

export function MockDeliveryStatusProvider({
  deliveryStatus,
  children,
}: PropsWithChildren<{ deliveryStatus: DeliveryStatus }>) {
  return (
    <Context.Provider value={{ deliveryStatus }}>{children}</Context.Provider>
  );
}

async function getDeliveryStatus(uuid: string): Promise<DeliveryStatus> {
  const res = await fetch(`${Config.apiUrl}/orders/${uuid}/delivery_status`);
  const json = await res.json();
  return json;
}

const fixture = {
  eta: "2024-04-18T17:00",
  carrier: "multientrega",
  order: {
    uuid: "6c1b1721-82ce-4455-b8a6-8acd744d83ed",
    simple_id: 1234,
    payment_method: "yappy",
    total: 2170,
    fulfillment_option: {
      description: "Zona 1",
      price: 100,
      currency: "usd",
    },
    cart: {
      discount: { code: "CUANTO10", percentage: 10 },
      discount_amount: 230,
      items_total: 2300,
      items: [
        {
          uuid: "1",
          name: "Zapatos hipster",
          quantity: 2,
          price: 1000,
          currency: "usd",
          image_url:
            "https://cuanto-products.nyc3.digitaloceanspaces.com/618d4d69-f539-44c1-b56e-84c2ae94a18e",
          images: [],
          variant_options: { color: "rojo", size: "42" },
        },
        {
          uuid: "2",
          name: "cosa con nombre largo y sin variante",
          quantity: 1,
          price: 100,
          currency: "usd",
          image_url:
            "https://cuanto-products.nyc3.digitaloceanspaces.com/618d4d69-f539-44c1-b56e-84c2ae94a18e",
          images: [],
          variant_options: {},
        },
        {
          uuid: "3",
          name: "otra cosa con nombre largo y con variante",
          quantity: 1,
          price: 200,
          currency: "usd",
          image_url:
            "https://cuanto-products.nyc3.digitaloceanspaces.com/618d4d69-f539-44c1-b56e-84c2ae94a18e",
          images: [],
          variant_options: { color: "azul", tamaño: "grande" },
        },
      ],
    },
    merchant: {
      name: "La Caldera",
      avatar_url:
        "https://cuanto-user-avatars.sfo2.digitaloceanspaces.com/841e400e-0a13-40e2-ae09-a2d253643bb9",
    },
  },
  tracking_status: {
    status: "pre_processing",
    substatus: "manual_payment_pending",
    created_at: "2024-04-18T17:00",
    label: "En camino",
    details: "Tu pedido esta en camino",
    actions: [
      {
        label: "Ayuda",
        primary: false,
        type: "send_whatsapp",
        params: { whatsapp: "", prefilled_text: "..." },
      },
      {
        label: "Llenar validación",
        type: "fill_validation",
        primary: true,
        params: {
          consumer_validation_urls: {
            en: "https://www.google.com",
            es: "https://www.google.com",
          },
        },
      },
    ],
  },
  tracking_history: [
    {
      status: "pre_transit",
      substatus: "information_received",
      created_at: "2024-04-18T17:00",
      label: "Información recibida",
    },
  ],
};
